import type { NotEmptyObject } from '@lemonade-hq/ts-helpers';
import type { IconName, IconProps, ResponsiveValues } from '@lemonade-hq/uniclient-theme';
import type { IntRange } from 'type-fest';
import type { UniclientAction } from './action';
import type { TagComposerProps } from './components/tag';
import type { HeaderAction, HeaderActionType, HeaderMenuProps } from './frame';
import { PredefinedMenuOptionType } from './frame';

// Formatted Text
export type FormattedTextStyles = 'bold' | 'disabled' | 'italic' | 'lineThroughDisabled' | 'lineThroughEnabled';

export interface ActionItemProps {
  readonly type: 'action';
  readonly action: UniclientAction;
}

export interface IconItemProps {
  readonly type: 'icon';
  readonly icon: IconName;
  readonly size?: IconProps['size'];
  readonly variant?: IconProps['variant'];
}

export type BaseFormattedTextItem = {
  readonly text: string;
  readonly tooltip?: string;
} & (ActionItemProps | { readonly style?: FormattedTextStyles });

export type FormattedTextItem = BaseFormattedTextItem | IconItemProps;

export interface NewlineProps {
  readonly type: 'newline';
  readonly content?: never; // explicitly indicating no content for newline
}

export type FormattedTextArray = FormattedTextItem | NewlineProps | TagComposerProps;

export type FormattedText = FormattedTextItem | string | [FormattedTextItem, ...FormattedTextArray[]];

const isNonNullObject = (x: unknown): x is FormattedTextArray => typeof x === 'object' && x !== null;
const hasType = (x: unknown): x is ActionItemProps | IconItemProps | NewlineProps | TagComposerProps =>
  isNonNullObject(x) && 'type' in x;

export const isTextProps = (x: unknown): x is BaseFormattedTextItem =>
  isNonNullObject(x) && 'text' in x && typeof x.text === 'string';

export const isNewLineProps = (x: unknown): x is NewlineProps => hasType(x) && x.type === 'newline';

export const isTagProps = (x: unknown): x is TagComposerProps => hasType(x) && x.type === 'tag';

export const isIconProps = (x: unknown): x is IconItemProps => hasType(x) && x.type === 'icon';

// Grid Columns
export type Columns = ResponsiveValues<number> | number;

export type GridResponsiveColumns =
  | IntRange<2, 7>
  | NotEmptyObject<{ readonly md?: IntRange<2, 5>; readonly lg?: IntRange<2, 7> }>;

export function isHeaderAction(action: HeaderActionType | PredefinedMenuOptionType): action is HeaderAction {
  return typeof action === 'object' && !('subActions' in action) && 'type' in action;
}

export function isHeaderMenuProps(action: HeaderActionType | PredefinedMenuOptionType): action is HeaderMenuProps {
  return typeof action === 'object' && 'subActions' in action;
}

export function isPredefinedMenuOptionType(
  action: HeaderActionType | PredefinedMenuOptionType,
): action is PredefinedMenuOptionType {
  return typeof action === 'string' && Object.values(PredefinedMenuOptionType).includes(action);
}
